import React from "react";

import { ThemeProvider } from "@mui/material";
import * as Sentry from "@sentry/nextjs";
import { GetServerSideProps, GetServerSidePropsContext } from "next";
import Error from "next/error";
import Head from "next/head";
import { Router } from "next/router";
import { SessionProvider } from "next-auth/react";
import nProgress from "nprogress";
import { SWRConfig } from "swr";

import { AlertContextProvider } from "context/alertcontext";
import AccessToken from "src/common/components/AccessToken";
import Auth from "src/common/components/Auth";
import ClientConfig from "src/common/components/ClientConfig";
import ErrorFallback from "src/common/components/ErrorFallback";
import Layout from "src/common/components/Layout";
import LoadingScreen from "src/common/components/LoadingScreen";
import MyFeatures from "src/common/components/MyFeatures";
import MyOrg from "src/common/components/MyOrg";
import MyPermissions from "src/common/components/MyPermissions";
import MyInfo from "src/common/components/MyUser";
import Domain from "src/common/components/session/Domain";
import { makeUserFriendly } from "src/lib/error";
import Styles from "styles/global";
import theme from "theme";
import "react-toastify/dist/ReactToastify.css";
import "styles/loader.css";
import "styles/nprogress.css";
import "styles/globals.css";

Router.events.on("routeChangeStart", nProgress.start);
Router.events.on("routeChangeError", nProgress.done);
Router.events.on("routeChangeComplete", nProgress.done);

function Fallback() {
  return <ErrorFallback sx={{ margin: "100px 20px 0px 20px" }}>Uh-oh! Something went wrong!</ErrorFallback>;
}

function MyApp({ Component, pageProps: { session, ...pageProps } }) {
  if (pageProps.error) {
    Sentry.captureException(pageProps.error);
    return <Error statusCode={pageProps.error?.statusCode || 500} title={makeUserFriendly(pageProps.error?.message)} />;
  }

  return (
    <Sentry.ErrorBoundary fallback={Fallback} showDialog>
      <SWRConfig
        value={{
          onError: (error) => {
            Sentry.captureException(error);
          },
        }}
      >
        <Head>
          <title>Metworx</title>
          <meta name="viewport" content="minimum-scale=1, initial-scale=1, width=device-width" />
        </Head>
        <ThemeProvider theme={theme}>
          <Styles />
          <SessionProvider session={session}>
            {Component.noLayout ? (
              <Component {...pageProps} />
            ) : (
              <Domain>
                <Auth>
                  <AccessToken>
                    <ClientConfig>
                      <MyInfo>
                        <MyPermissions>
                          <MyFeatures>
                            <AlertContextProvider>
                              <MyOrg>
                                <LoadingScreen>
                                  <Layout>
                                    <Component {...pageProps} />
                                  </Layout>
                                </LoadingScreen>
                              </MyOrg>
                            </AlertContextProvider>
                          </MyFeatures>
                        </MyPermissions>
                      </MyInfo>
                    </ClientConfig>
                  </AccessToken>
                </Auth>
              </Domain>
            )}
          </SessionProvider>
        </ThemeProvider>
      </SWRConfig>
    </Sentry.ErrorBoundary>
  );
}

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
  const cookies = context.req.cookies;
  console.log("cookies: ", cookies["email"]);

  try {
    const email = cookies["email"];
    return {
      props: { email },
    };
  } catch (error) {
    return {
      props: {
        error: { ...error },
      },
    };
  }
};

export default MyApp;
